import agent from "../agent/agent";
import {
  getCameraAddressId,
  getCameraLocationLabel,
  getComplexAddressId,
  getComplexLocationLabel,
  getLocationRecordId,
} from "./addressFields";

/** ID сенсора из записи проезда (материалы) */
export const getReportSensorTargetId = (reportItem) => {
  const id = reportItem?.sensor_id ?? reportItem?.sensor?.id;
  if (id == null || String(id).trim() === "") return null;
  return String(id);
};

export const isStationaryComplex = (item) =>
  String(item?.class || "").toLowerCase().includes("стационарный");

export const isCameraEntity = (item) => item?.complex_cam === "camera";

/** Найти адрес в списке по target_id (address_id / address_cam_id / id) */
export const matchLocationByTargetId = (items, targetId) => {
  const id = targetId == null ? "" : String(targetId).trim();
  if (!id) return null;
  return (
    (items || []).find((item) => {
      const recordId = getLocationRecordId(item);
      if (recordId && recordId === id) return true;
      const candidates = [
        item?.address_id,
        item?.address_cam_id,
        item?.id,
        item?.new_address?.address_id,
        item?.new_address_cam?.address_cam_id,
      ];
      if (candidates.some((value) => value != null && String(value).trim() === id)) return true;
      return (item?.devices || []).some((device) =>
        [device?.address_id, device?.address_cam_id].some(
          (value) => value != null && String(value).trim() === id
        )
      );
    }) || null
  );
};

export const fetchSensorByTargetId = async (contractId, targetId) => {
  if (!contractId || !targetId) return null;
  const id = String(targetId).trim();
  const res = await agent
    .get(
      `/operator/sensors?contract_id=${encodeURIComponent(contractId)}&target_id=${encodeURIComponent(id)}&limit=50&page=1`
    )
    .catch(() => null);
  const items = res?.data?.data || [];
  return (
    items.find((item) => String(item?.id ?? "").trim() === id) ||
    items.find((item) => String(item?.complex_id ?? "").trim() === id) ||
    (items.length === 1 ? items[0] : null)
  );
};

const sameSerial = (item, serial) =>
  String(item?.complex_serial || "").trim().toLowerCase() === String(serial || "").trim().toLowerCase();

/** Найти комплекс по серийному номеру, если target_id из сверки уже не совпадает */
export const fetchSensorBySerial = async (contractId, serial) => {
  const needle = String(serial || "").trim();
  if (!contractId || !needle) return null;
  const res = await agent
    .get(
      `/operator/sensors?contract_id=${encodeURIComponent(contractId)}&complex_serial=${encodeURIComponent(needle)}&limit=50&page=1`
    )
    .catch(() => null);
  const items = res?.data?.data || [];
  const exact = items.filter((item) => sameSerial(item, needle));
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return exact.find((item) => !isCameraEntity(item)) || exact[0];
  return null;
};

/**
 * Сайдбар / deep-link: локация по target_id.
 * limit — как у таблицы на странице, иначе x-pagination-page не совпадёт со списком.
 * @returns {{ item: object|null, page: number|null }}
 */
export const fetchLocationByTargetId = async (contractId, targetId, { limit = 15 } = {}) => {
  if (!contractId || !targetId) return { item: null, page: null };
  const id = String(targetId).trim();
  const pageLimit = Number.parseInt(limit, 10);
  const safeLimit = Number.isFinite(pageLimit) && pageLimit > 0 ? pageLimit : 15;
  const res = await agent
    .get(
      `/operator/locations?contract_id=${encodeURIComponent(contractId)}&target_id=${encodeURIComponent(id)}&limit=${safeLimit}&page=1`
    )
    .catch(() => null);
  const pageRaw = Number.parseInt(res?.headers?.["x-pagination-page"] || "", 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : null;
  const items = res?.data?.data || [];
  const matched = matchLocationByTargetId(items, id);
  if (matched) return { item: matched, page };
  // Одна запись — фильтр target_id сработал, даже если id в ответе уже другой.
  // Общий список (API проигнорировал target_id) сюда не попадает: там больше одной записи.
  if (items.length === 1) return { item: items[0], page };
  return { item: null, page };
};

const findCameraInComplex = (complex, cameraSerial) => {
  const serial = String(cameraSerial || "").trim();
  if (!serial) return null;
  return (complex?.sensor_cameras || []).find(
    (cam) => String(cam?.cam_serial || "").trim() === serial
  ) || null;
};

/** Состояние сайдпанели «Комплексы» по данным сенсора */
export const getComplexDrawerState = (sensor) => {
  if (!sensor) return { activeTab: "info", selectedCamera: null };

  if (isCameraEntity(sensor)) {
    return { activeTab: "cameras", selectedCamera: null, pendingCameraSerial: sensor.complex_serial };
  }
  if (isStationaryComplex(sensor)) {
    return { activeTab: "cameras", selectedCamera: null };
  }
  return { activeTab: "directions", selectedCamera: null };
};

/**
 * Разрешает переход в «Комплексы» по target_id (из материалов / адресов).
 * Для камеры подгружает родительский комплекс и выбирает камеру в таблице.
 */
export const resolveComplexNavigation = async (contractId, targetId) => {
  const sensor = await fetchSensorByTargetId(contractId, targetId);
  if (!sensor) return null;

  if (isCameraEntity(sensor)) {
    const parentId = sensor.parent_id ?? sensor.parent_sensor_id ?? sensor.complex_sensor_id;
    if (parentId) {
      const parent = await fetchSensorByTargetId(contractId, parentId);
      if (parent) {
        const selectedCamera =
          findCameraInComplex(parent, sensor.complex_serial) ||
          { cam_serial: sensor.complex_serial };
        return {
          drawerItem: parent,
          drawerState: { activeTab: "cameras", selectedCamera },
        };
      }
    }
    return {
      drawerItem: sensor,
      drawerState: { activeTab: "directions", selectedCamera: null },
    };
  }

  return {
    drawerItem: sensor,
    drawerState: getComplexDrawerState(sensor),
  };
};

/**
 * Сайдпанель «Комплексы» по deep-link.
 * Если в ссылке есть target_id, открываем только его. Серийный номер — запасной
 * путь, когда id в ссылке нет.
 */
export const resolveComplexDrawerFromSearch = async (contractId, searchParams) => {
  if (!contractId || searchParams?.get("open") !== "1") return null;

  const targetId = String(searchParams.get("target_id") || "").trim();
  if (targetId) {
    return resolveComplexNavigation(contractId, targetId);
  }

  const serial = String(
    searchParams.get("focus_serial") || searchParams.get("complex_serial") || ""
  ).trim();
  if (!serial) return null;
  const sensor = await fetchSensorBySerial(contractId, serial);
  if (!sensor) return null;
  const sensorId = String(sensor?.id ?? sensor?.complex_id ?? "").trim();
  if (sensorId) {
    const resolved = await resolveComplexNavigation(contractId, sensorId);
    if (resolved) return resolved;
  }
  return {
    drawerItem: sensor,
    drawerState: getComplexDrawerState(sensor),
  };
};

/** Переход в Комплексы: target_id и/или серийный номер. focus_serial не фильтрует таблицу */
export const buildComplexUrl = ({ targetId, serial } = {}) => {
  const params = new URLSearchParams();
  const id = String(targetId ?? "").trim();
  const serialText = String(serial ?? "").trim();
  if (id) params.set("target_id", id);
  if (serialText) params.set("focus_serial", serialText);
  if (!id && !serialText) return null;
  params.set("open", "1");
  return `/complex?${params.toString()}`;
};

/** Переход в Адреса: param всегда target_id, значение — из address_id / address_cam_id */
export const buildAddressesUrlByTargetId = (targetId, { location } = {}) => {
  if (!targetId) return null;
  const params = new URLSearchParams();
  params.set("target_id", String(targetId).trim());
  params.set("open", "1");
  const locationText = location == null ? "" : String(location).trim();
  // focus_location — подпись для сообщения «адрес не найден», не фильтр таблицы
  if (locationText) {
    params.set("focus_location", locationText);
  }
  return `/addresses?${params.toString()}`;
};

export const getMaterialsToAddressesUrl = (reportItem) => {
  const addressId =
    reportItem?.address_id ??
    reportItem?.sensor?.address_id ??
    reportItem?.address_cam_id ??
    reportItem?.sensor?.address_cam_id ??
    reportItem?.sensor?.new_address?.address_id ??
    reportItem?.sensor?.new_address_cam?.address_cam_id;
  if (addressId == null || String(addressId).trim() === "") return null;
  return buildAddressesUrlByTargetId(String(addressId).trim(), {
    location: reportItem?.sensor_location ?? reportItem?.sensor?.location,
  });
};

export const getMaterialsToComplexUrl = (reportItem) => {
  const targetId = getReportSensorTargetId(reportItem);
  if (targetId) {
    return `/complex?target_id=${encodeURIComponent(targetId)}&open=1`;
  }
  if (reportItem?.sensor_serial) {
    return `/complex?complex_serial=${encodeURIComponent(reportItem.sensor_serial)}&open=1`;
  }
  return null;
};

export const canNavigateToAddresses = (reportItem) => Boolean(getMaterialsToAddressesUrl(reportItem));
export const canNavigateToComplex = (reportItem) => Boolean(getMaterialsToComplexUrl(reportItem));

/** ID сенсора/комплекса/камеры для target_id */
export const getEntityTargetId = (entity) => {
  const id = entity?.id ?? entity?.cam_id ?? entity?.sensor_id;
  if (id == null || String(id).trim() === "") return null;
  return String(id);
};

/** Комплексы → Адреса: target_id = значение address_id */
export const getSensorToAddressesUrl = (sensor) => {
  const addressId = getComplexAddressId(sensor);
  if (!addressId) return null;
  return buildAddressesUrlByTargetId(addressId, {
    location: getComplexLocationLabel(sensor),
  });
};

/**
 * Камеры → Адреса: target_id = address_cam_id.
 * Второй аргумент (complex) — запасной address_id, если у камеры нет своего.
 */
export const getCameraToAddressesUrl = (camera, complex = null) => {
  const cameraAddressId = getCameraAddressId(camera);
  if (cameraAddressId) {
    return buildAddressesUrlByTargetId(cameraAddressId, {
      location: getCameraLocationLabel(camera),
    });
  }
  const complexAddressId = getComplexAddressId(complex);
  if (!complexAddressId) return null;
  return buildAddressesUrlByTargetId(complexAddressId, {
    location: getComplexLocationLabel(complex),
  });
};

export const canNavigateSensorToAddresses = (sensor) => Boolean(getSensorToAddressesUrl(sensor));

/** Комплексы → Комплексы (новая вкладка с сайдпанелью) или Адреса → Комплексы */
export const getSensorToComplexUrl = (sensor) => {
  const targetId = getEntityTargetId(sensor);
  if (targetId) {
    return `/complex?target_id=${encodeURIComponent(targetId)}&open=1`;
  }
  if (sensor?.complex_serial) {
    return `/complex?complex_serial=${encodeURIComponent(sensor.complex_serial)}&open=1`;
  }
  return null;
};

/** Адреса → Комплексы: target_id = id устройства (device.id) или legacy complex_id */
export const getDeviceToComplexUrl = (device) => {
  const deviceId = String(device?.id ?? "").trim();
  if (!deviceId) return null;
  return `/complex?target_id=${encodeURIComponent(deviceId)}&open=1`;
};

export const getAddressToComplexUrl = (addressItem, device = null) => {
  if (device) return getDeviceToComplexUrl(device);
  const devices = Array.isArray(addressItem?.devices) ? addressItem.devices : [];
  if (devices.length === 1) return getDeviceToComplexUrl(devices[0]);
  const complexId = String(addressItem?.complex_id ?? "").trim();
  if (!complexId) return null;
  return `/complex?target_id=${encodeURIComponent(complexId)}&open=1`;
};

export const canNavigateSensorToComplex = (sensor) => Boolean(getSensorToComplexUrl(sensor));
export const canNavigateAddressToComplex = (addressItem, device = null) =>
  Boolean(getAddressToComplexUrl(addressItem, device));
